<template>
  <div
    class="ss-dashboard-date-range-picker"
    :class="{
      'ss-dashboard-date-range-picker--active': $data.$_ssDRP_showPicker
    }"
  >
    <!-- Selected Range Display -->
    <div
      @click="$data.$_ssDRP_showPicker = true"
      class="ss-dashboard-date-range-picker__text"
    >
      <!-- Preset Label -->
      <span
        v-if="$data.$_ssDRP_currentRangePresetIndex !== null"
        class="ss-dashboard-date-range-picker__label"
      >
        {{ $data.$_ssDRP_rangePresets[$data.$_ssDRP_currentRangePresetIndex].label }}
      </span>

      <!-- Actual Date Range -->
      <span class="ss-dashboard-date-range-picker__value">
        {{ this.$_ssDRP_formatDateLabel($data.$_ssDRP_currentStartDate) }}〜{{ this.$_ssDRP_formatDateLabel($data.$_ssDRP_currentEndDate) }}
      </span>

      <!-- Icon -->
      <oct-icon
        icon="expand-more"
        class="ss-dashboard-date-range-picker__icon"
      />
    </div>

    <!-- Picker Menu -->
    <div
      class="ss-dashboard-date-range-picker-menu"
      v-show="$data.$_ssDRP_showPicker"
    >
      <div class="ss-dashboard-date-range-picker-menu__content">
        <!-- Preset List -->
        <div class="ss-dashboard-date-range-picker-menu__presets">
          <div class="ss-dashboard-date-range-picker-menu__presets-header">
            カスタム
          </div>

          <span
            v-for="(item, index) in $data.$_ssDRP_rangePresets"
            :key="index"
            :aria-selected="index === $data.$_ssDRP_selectedRangePresetIndex"
            class="ss-dashboard-date-range-picker-menu__presets-item"
            @click="$_ssDRP_onSelectPreset(index)"
          >
            {{ item.label }}
          </span>
        </div>

        <!-- Indicator & Calendar -->
        <div class="ss-dashboard-date-range-picker-menu__input">
          <!-- Current Range Indicator -->
          <div class="ss-dashboard-date-range-picker-menu-range">
            <div class="ss-dashboard-date-range-picker-menu-range__col">
              開始日

              <span class="ss-dashboard-date-range-picker-menu-range__value">
                {{ $_ssDRP_startDateLabel }}
              </span>
            </div>

            <div class="ss-dashboard-date-range-picker-menu-range__col">
              終了日

              <span class="ss-dashboard-date-range-picker-menu-range__value">
                {{ $_ssDRP_endDateLabel }}
              </span>
            </div>
          </div>

          <!-- Calendar -->
          <div class="ss-dashboard-date-range-picker-menu__calendar">
            <input type="text" id="ss-dashboard-date-range-picker__flatpickr" />
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="ss-dashboard-date-range-picker-menu__actions">
        <oct-button
          block
          outline
          size="medium"
          @click.native="cancel"
        >
          キャンセル
        </oct-button>

        <oct-button
          block
          size="medium"
          @click.native="submit"
        >
          OK
        </oct-button>
      </div>
    </div>
  </div>
</template>

<script>
import OctIcon from '@/components/icon/OctIcon.vue'
import OctButton from '@/components/button/OctButton.vue'

export default {
  name: 'SSDashboardDateRangePicker',
  components: {
    OctIcon,
    OctButton,
  },
  data() {
    const today = new Date();
    const sevenDaysAgo= new Date();
    const thirtyDaysAgo = new Date();
    const ninetyDaysAgo = new Date();
    const oneHundredEightyDaysAgo = new Date();
    const threeHundredSixtyFiveDaysAgo = new Date();

    const startOfThisWeek = new Date(today);
    startOfThisWeek.setDate(today.getDate() - today.getDay());

    const endOfLastWeek = new Date(startOfThisWeek);
    endOfLastWeek.setDate(startOfThisWeek.getDate() - 1);
    const startOfLastWeek = new Date(endOfLastWeek);
    startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);

    sevenDaysAgo.setDate(today.getDate() - 7);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    ninetyDaysAgo.setDate(today.getDate() - 90);
    oneHundredEightyDaysAgo.setDate(today.getDate() - 180);
    threeHundredSixtyFiveDaysAgo.setDate(today.getDate() - 365);

    return {
      /** Flatpickr instance. */
      $_ssDRP_flatpickr: null,
      /**
       * Inclusive start of the selected range (`YYYY-MM-DD`); initialized to 30 days ago.
       * @type {string}
       */
      $_ssDRP_startDate: this.$_ssDRP_formatDate(thirtyDaysAgo),
      /**
       * Inclusive end of the selected range (`YYYY-MM-DD`); initialized to today.
       * @type {string}
       */
      $_ssDRP_endDate: this.$_ssDRP_formatDate(today),
      /**
       * Index of the selected range preset.
       * @type {number}
       */
      $_ssDRP_selectedRangePresetIndex: 3,
      /**
       * Range presets.
       * @type {Array<{label: string, value: [string, string]}>}
       */
      $_ssDRP_rangePresets: [
        {
          'label': '今週',
          'value': [this.$_ssDRP_formatDate(startOfThisWeek), this.$_ssDRP_formatDate(today)],
        },
        {
          'label': '先週',
          'value': [this.$_ssDRP_formatDate(startOfLastWeek), this.$_ssDRP_formatDate(endOfLastWeek)],
        },
        {
          'label': '過去7日間',
          'value': [this.$_ssDRP_formatDate(sevenDaysAgo), this.$_ssDRP_formatDate(today)],
        },
        {
          'label': '過去30日間',
          'value': [this.$_ssDRP_formatDate(thirtyDaysAgo), this.$_ssDRP_formatDate(today)],
        },
        {
          'label': '過去90日間',
          'value': [this.$_ssDRP_formatDate(ninetyDaysAgo), this.$_ssDRP_formatDate(today)],
        },
        {
          'label': '過去180日間',
          'value': [this.$_ssDRP_formatDate(oneHundredEightyDaysAgo), this.$_ssDRP_formatDate(today)],
        },
        {
          'label': '過去365日間',
          'value': [this.$_ssDRP_formatDate(threeHundredSixtyFiveDaysAgo), this.$_ssDRP_formatDate(today)],
        },
      ],
      /**
       * Current range preset.
       * @type {number}
       */
      $_ssDRP_currentRangePresetIndex: null,
      /**
       * Current start date.
       * @type {string}
       */
      $_ssDRP_currentStartDate: null,
      /**
       * Current end date.
       * @type {string}
       */
      $_ssDRP_currentEndDate: null,
      /**
       * Whether the picker menu is visible.
       * @type {boolean}
       */
      $_ssDRP_showPicker: false,
    };
  },
  computed: {
    /**
     * Label for the start date.
     * @type {string}
     */
    $_ssDRP_startDateLabel: vm => vm.$_ssDRP_formatDateLabel(vm.$data.$_ssDRP_startDate),
    /**
     * Label for the end date.
     * @type {string}
     */
    $_ssDRP_endDateLabel: vm => vm.$_ssDRP_formatDateLabel(vm.$data.$_ssDRP_endDate),
  },
  methods: {
    /**
     * Called when the user confirms the range.
     * Closes the picker and emits `change` with `{ startDate, endDate }`. 
     *
     * @fires change
     */
    submit() {
      this.$data.$_ssDRP_showPicker = false;
      this.$_ssDRP_syncCurrentValues();

      this.$emit('change', {
        startDate: this.$data.$_ssDRP_startDate,
        endDate: this.$data.$_ssDRP_endDate
      });
    },
    /**
     * Cancel button click handler.
     * Closes the picker and resets the selected values.
     */
    cancel() {
      this.$data.$_ssDRP_showPicker = false;
      this.$_ssDRP_resetSelectedValues();
    },
    /**
     * Synchronizes the current values.
     */
    $_ssDRP_syncCurrentValues() {
      this.$data.$_ssDRP_currentStartDate = this.$data.$_ssDRP_startDate;
      this.$data.$_ssDRP_currentEndDate = this.$data.$_ssDRP_endDate;
      this.$data.$_ssDRP_currentRangePresetIndex = this.$data.$_ssDRP_selectedRangePresetIndex;
    },
    /**
     * Resets the selected values.
     */
    $_ssDRP_resetSelectedValues() {
      this.$data.$_ssDRP_selectedRangePresetIndex = this.$data.$_ssDRP_currentRangePresetIndex;
      this.$data.$_ssDRP_startDate = this.$data.$_ssDRP_currentStartDate;
      this.$data.$_ssDRP_endDate = this.$data.$_ssDRP_currentEndDate;
    },
    /**
     * Preset list click handler: applies the preset dates and refreshes Flatpickr.
     *
     * @param {number} index Preset index in `$_ssDRP_rangePresets`.
     */
    $_ssDRP_onSelectPreset(index) {
      this.$_ssDRP_applyPreset(index);
      this.$_ssDRP_updateFlatpickr();
    },
    /**
     * Sets `$_ssDRP_selectedRangePresetIndex`, `$_ssDRP_startDate`, and `$_ssDRP_endDate` from a preset.
     * Does not update Flatpickr; call `$_ssDRP_updateFlatpickr` after if the calendar should reflect the change.
     *
     * @param {number} index Preset index in `$_ssDRP_rangePresets`.
     */
    $_ssDRP_applyPreset(index) {
      this.$data.$_ssDRP_selectedRangePresetIndex = index;
      this.$data.$_ssDRP_startDate = this.$data.$_ssDRP_rangePresets[index].value[0];
      this.$data.$_ssDRP_endDate = this.$data.$_ssDRP_rangePresets[index].value[1];
    },
    /**
     * Date range change handler.
     * Updates the start and end dates.
     * @param {Array<Date>} selectedDates - The selected dates.
     */
    $_ssDRP_onChangeDateRange(selectedDates) {
      if (selectedDates.length !== 2) return;

      this.$data.$_ssDRP_startDate = this.$_ssDRP_formatDate(selectedDates[0]);
      this.$data.$_ssDRP_endDate = this.$_ssDRP_formatDate(selectedDates[1]);

      this.$data.$_ssDRP_selectedRangePresetIndex = null;
    },
    /**
     * Syncs the Flatpickr range UI with the current `$_ssDRP_startDate` / `$_ssDRP_endDate`.
     * Does nothing when `window.flatpickr` is unavailable.
     */
    $_ssDRP_updateFlatpickr() {
      if (!window.flatpickr) return;

      this.$data.$_ssDRP_flatpickr.setDate([this.$data.$_ssDRP_startDate, this.$data.$_ssDRP_endDate]);
    },
    /**
     * Format a date as YYYY-MM-DD.
     *
     * @param {Date} date
     * @returns {string}
     */
    $_ssDRP_formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    /**
     * Format a date for display.
     *
     * @param {String} date
     * @returns {string}
     */
    $_ssDRP_formatDateLabel(date) {
      date = new Date(date);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    },
  },
  mounted() {
    // Initialize Flatpickr
    if (window.flatpickr) {
      this.$data.$_ssDRP_flatpickr = window.flatpickr('#ss-dashboard-date-range-picker__flatpickr', {
        mode: 'range',
        locale: 'ja',
        inline: true,
        maxDate: 'today',
        onChange: this.$_ssDRP_onChangeDateRange,
      });

      this.$_ssDRP_onSelectPreset(this.$data.$_ssDRP_selectedRangePresetIndex);
      this.$_ssDRP_syncCurrentValues();
    }

    // Fire initial change event
    this.submit();
  },
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.ss-dashboard-date-range-picker {
  position: relative;
  display: flex;
  align-items: center;
  gap: oct-rem(8);
  min-width: oct-rem(382+32);
  height: oct-rem(40);
  padding: 0 oct-rem(16);
  border: oct-rem(1) solid $oct-theme--neutral-20;

  &--active {
    border-color: $oct-theme--neutral;
  }
}

.ss-dashboard-date-range-picker__text {
  display: flex;
  align-items: center;
  gap: oct-rem(16);
}

.ss-dashboard-date-range-picker__icon {
  margin: oct-rem(-6);
  width: oct-rem(28);
  height: oct-rem(28);
  fill: $oct-theme--neutral;
}

.ss-dashboard-date-range-picker__label {
  font-size: oct-rem(12);
  line-height: 1;
  padding: oct-rem(4);
  background-color: $oct-theme--neutral-10;
}

.ss-dashboard-date-range-picker-menu {
  position: absolute;
  right: oct-rem(-1);
  top: 100%;
  margin-top: oct-rem(16);
  background-color: $oct-theme--surface;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.10);
  z-index: 1;

  &::v-deep .flatpickr-day.selected,
  &::v-deep .flatpickr-day.startRange {
    background-color: $oct-theme--primary;
  }

  &::v-deep .flatpickr-calendar {
    box-shadow: none !important;
  }
}

.ss-dashboard-date-range-picker-menu__content {
  display: flex;
  padding: oct-rem(32) oct-rem(32) oct-rem(64);
}

.ss-dashboard-date-range-picker-menu__presets {
  display: flex;
  flex-direction: column;
  gap: oct-rem(16);
  width: oct-rem(144+32);
  height: 100%;
  padding-right: oct-rem(32);
}

.ss-dashboard-date-range-picker-menu__presets-header {
  font-size: oct-rem(14);
  line-height: 1;
  padding-bottom: oct-rem(16);
  border-bottom: oct-rem(1) solid $oct-theme--neutral-20;
}

.ss-dashboard-date-range-picker-menu__presets-item {
  font-size: oct-rem(14);
  line-height: 1;
  display: block;
  cursor: pointer;

  &:hover, &[aria-selected="true"] {
    font-weight: 600;
    color: $oct-theme--primary;
  }
}

.ss-dashboard-date-range-picker-menu__input {
  display: flex;
  flex-direction: column;
  gap: oct-rem(32);
  padding-left: oct-rem(32);
  border-left: oct-rem(1) solid $oct-theme--neutral-20;
}

.ss-dashboard-date-range-picker-menu-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: oct-rem(24);
}

.ss-dashboard-date-range-picker-menu-range__col {
  font-size: oct-rem(12);
  line-height: 1;
  display: flex;
  flex-direction: column;
  gap: oct-rem(8);
}

.ss-dashboard-date-range-picker-menu-range__value {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: oct-rem(40);
  border: oct-rem(1) solid $oct-theme--neutral-20;
}

#ss-dashboard-date-range-picker__flatpickr {
  display: none;
}

.ss-dashboard-date-range-picker-menu__actions {
  display: flex;
  justify-content: flex-end;
  gap: oct-rem(16);
  padding: oct-rem(32);
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.10);

  .oct-button {
    width: oct-rem(80+48);
  }
}
</style>
